import { capitalizeName, edge, parseKickoff, parseOdds, parseProb } from './format';

// The betting rule the model is tuned for: at least a 3% edge at odds of 3 or less, the edge measured
// with the blended (bet) probabilities.
export const VALUE_EDGE = 0.03;
export const VALUE_MAX_ODDS = 3;

export function isValue(outcome) {
  return outcome && outcome.edge !== null && outcome.edge >= VALUE_EDGE && outcome.odds !== null
    && outcome.odds <= VALUE_MAX_ODDS;
}

// API row -> the row plus parsed numbers: kickoff (Date), outcomes (home, draw, away) with one
// probability (prob), odds, fair odds, edge and target odds (where the edge reaches VALUE_EDGE; above
// VALUE_MAX_ODDS the rule never bets) from prob, the best-edge outcome, the model's bet
// (pick, amount) and a search string. prob is the blend the bet is decided on (Bet*Prob: the market
// adjusted by the rating engine and the stats model) when the row has it, else the published one.
// The production server stores only one set, the blend, and sends it as both.
// Each outcome keeps the fields the pick form posts (Result, ResultValue, Odds, team codes, ...).
export function normalizeMatch(row, { homeImage = 'HomeTeamImagePath', awayImage = 'AwayTeamImagePath' } = {}) {
  // The production API (ProductionWebCode/server.js) keeps League as display text ("premier league
  // england") for the old site and sends the code apart as LeagueCode; server-copa sends the code as League.
  const item = row.LeagueCode ? { ...row, League: row.LeagueCode } : row;
  const shared = {
    HomeTeamCode: item.HomeTeamCode, AwayTeamCode: item.AwayTeamCode, MatchTime: item.MatchTime,
    MatchTimeRaw: item.MatchTimeRaw || item.MatchTime, League: item.League, PredId: item.PredId,
    HomeTeamImagePath: item[homeImage], AwayTeamImagePath: item[awayImage],
    CountryImagePath: item.CountryImagePath || item.LeagueImagePath,
  };
  const outcome = (key, result, label, prefix, image) => {
    const blend = parseProb(item[`Bet${prefix}Prob`]);
    const prob = blend !== null ? blend : parseProb(item[`${prefix}Prob`]);
    const odds = parseOdds(item[`${prefix}Odds`]);
    const targetOdds = prob ? (1 + VALUE_EDGE) / prob : null;
    return {
      ...shared, key, label, Result: result, ResultValue: key, Image: image || '',
      Prob: item[`${prefix}Prob`], Odds: odds, Favor: item[`${prefix}Favor`],
      OddsPred: prob ? 1 / prob : null,
      targetOdds,
      targetOverLimit: targetOdds !== null && targetOdds > VALUE_MAX_ODDS,
      prob, odds, edge: edge(prob, odds),
      isPrediction: item.Prediction === key,
      isResult: item.ActualResult && item.ActualResult !== 'N' ? item.ActualResult === key : null,
    };
  };
  const outcomes = [
    outcome('H', item.HomeTeamCode, capitalizeName(item.HomeTeamCode), 'Win', item[homeImage]),
    outcome('D', 'Draw', 'Draw', 'Draw', ''),
    outcome('A', item.AwayTeamCode, capitalizeName(item.AwayTeamCode), 'Loss', item[awayImage]),
  ];
  const withEdge = outcomes.filter((o) => o.edge !== null);
  const best = withEdge.length ? withEdge.reduce((a, b) => (b.edge > a.edge ? b : a)) : null;
  const odds = outcomes.map((o) => o.odds).filter((o) => o !== null);
  const amount = Number(item.Amount) || 0;
  const pick = amount > 0 ? outcomes.find((o) => o.isPrediction) || null : null;
  return {
    ...item,
    kickoff: parseKickoff(item.MatchTime),
    outcomes,
    best,
    pick,
    amount: pick ? amount : 0,
    // The model's bet: what it actually stakes on, not just a positive edge.
    value: Boolean(pick),
    favouriteOdds: odds.length ? Math.min(...odds) : null,
    leagueKey: `${item.Country || ''}/${item.League}`,
    settled: Boolean(item.ActualResult && item.ActualResult !== 'N'),
    search: `${item.HomeTeamCode} ${item.AwayTeamCode} ${item.League} ${item.Country || ''}`
      .toLowerCase().replace(/-/g, ' '),
  };
}

export const SORTS = [
  { label: 'Kick-off', value: 'kickoff' },
  { label: 'Biggest edge', value: 'edge' },
  { label: 'Shortest odds', value: 'odds' },
  { label: 'Longest odds', value: 'odds-desc' },
];

function compare(a, b) {
  if (a === null || a === undefined) {
    return b === null || b === undefined ? 0 : 1;
  }
  if (b === null || b === undefined) {
    return -1;
  }
  return a < b ? -1 : (a > b ? 1 : 0);
}

const time = (m) => (m.kickoff ? m.kickoff.getTime() : null);

export function sortMatches(matches, sort) {
  const rows = [...matches];
  const byKickoff = (a, b) => compare(time(a), time(b));
  const rules = {
    kickoff: byKickoff,
    edge: (a, b) => compare(b.best ? b.best.edge : null, a.best ? a.best.edge : null) || byKickoff(a, b),
    odds: (a, b) => compare(a.favouriteOdds, b.favouriteOdds) || byKickoff(a, b),
    'odds-desc': (a, b) => compare(b.favouriteOdds, a.favouriteOdds) || byKickoff(a, b),
  };
  return rows.sort(rules[sort] || byKickoff);
}

// league: one leagueKey or a list of them (empty = every league).
export function filterMatches(matches, { query = '', league = null, valueOnly = false }) {
  const words = query.toLowerCase().replace(/-/g, ' ').split(/\s+/).filter(Boolean);
  const leagues = (Array.isArray(league) ? league : [league]).filter(Boolean);
  return matches.filter((m) => (leagues.length === 0 || leagues.includes(m.leagueKey))
    && (!valueOnly || m.value)
    && words.every((w) => m.search.includes(w)));
}
